
import { useTranslation } from "react-i18next";
import { GroupDetails } from "@/components/groups/GroupDetails";
import { ExpensesSection } from "@/components/groups/ExpensesSection";
import { Settlements } from "@/components/groups/Settlements";
import { GroupStatusBanner } from "@/components/groups/GroupStatusBanner";
import { Member } from "@/types/member";
import { Expense } from "@/types/expense";
import { Settlement } from "@/types/payment";

interface GroupContentProps {
  groupId: string;
  groupName: string;
  groupStatus: string;
  expenses: Expense[];
  members: Member[];
  settlements: Settlement[];
  currencies: { code: string; symbol: string }[];
  showExpenseForm: boolean;
  showAddMember: boolean;
  showShareLink: boolean;
  session: any;
  editingExpenseId: string | null;
  expenseFormState: {
    title: string;
    amount: string;
    currency: string;
    paidBy: string;
    sharedWith: string[];
  };
  setShowExpenseForm: (show: boolean) => void;
  setShowAddMember: (show: boolean) => void;
  setShowShareLink: (show: boolean) => void;
  handleRemoveMember: (memberId: string) => Promise<void>;
  handleExpenseSubmit: (formData: {
    title: string;
    amount: string;
    currency: string;
    paidBy: string;
    sharedWith: string[];
  }) => Promise<void>;
  handleEditExpense: (expense: Expense) => void;
  handleDeleteExpense: (expenseId: string) => Promise<void>;
  handleSettlementUpdate: () => void;
  fetchExpenses: () => Promise<void>;
  fetchSettlements: () => Promise<void>;
  handleScanReceipt?: () => void; // Added new prop for receipt scanning
}

export const GroupContent = ({
  groupId,
  groupName,
  groupStatus,
  expenses,
  members,
  settlements,
  currencies,
  showExpenseForm,
  showAddMember,
  showShareLink,
  session,
  editingExpenseId,
  expenseFormState,
  setShowExpenseForm,
  setShowAddMember,
  setShowShareLink,
  handleRemoveMember,
  handleExpenseSubmit,
  handleEditExpense,
  handleDeleteExpense,
  handleSettlementUpdate,
  fetchExpenses,
  fetchSettlements,
  handleScanReceipt
}: GroupContentProps) => {
  const { t } = useTranslation();
  
  return (
    <div className="container mx-auto px-4 py-6 space-y-4">
      {/* Group status banner */}
      <GroupStatusBanner status={groupStatus} />
      
      {/* Group details and members */}
      <GroupDetails
        groupId={groupId}
        groupName={groupName}
        members={members}
        showAddMember={showAddMember}
        showShareLink={showShareLink}
        handleRemoveMember={handleRemoveMember}
        setShowAddMember={setShowAddMember}
        setShowShareLink={setShowShareLink}
      />
      
      {/* Expenses section */}
      <ExpensesSection
        expenses={expenses}
        members={members}
        currencies={currencies}
        groupId={groupId}
        showExpenseForm={showExpenseForm}
        editingExpenseId={editingExpenseId}
        expenseFormState={expenseFormState}
        onAddClick={() => setShowExpenseForm(true)}
        onCancel={() => setShowExpenseForm(false)}
        onSubmit={handleExpenseSubmit}
        onEditExpense={handleEditExpense}
        onDeleteExpense={handleDeleteExpense}
        onRefresh={fetchExpenses}
        onScanReceipt={handleScanReceipt} // Pass the scan receipt handler
      />

      {/* Settlements section - ALWAYS SHOWN */}
      <Settlements
        settlements={settlements}
        members={members}
        currencies={currencies}
        currentUserId={session?.user?.id}
        groupId={groupId}
        onSettlementUpdate={handleSettlementUpdate}
        onRefresh={fetchSettlements}
      />
    </div>
  );
};
